import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  buscarVeiculosEmAndamento,
  type VeiculoEmAndamento,
} from '../../../api/setorEventosApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import AndamentoVeiculoModal from '../../../components/modal/andamento-veiculo/AndamentoVeiculoModal';
import Paginacao from '../../../components/paginacao/Paginacao';
import { useSearchParams } from 'react-router-dom';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { notify } from '../../../shared/feedback/notify';
import SearchIcon from '../../../img/search-icon.svg';
import { SeloPosEventoPorOrigem } from '../../../components/selo-pos-evento/SeloPosEvento';
import '../../../shared/ui/badge-status.css';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
// Esqueleto, tabela em grid, controles e paginação são as classes compartilhadas do sistema
// (`ConteDo`, `recepcao-*`, `VisualizarOrdenar`, `CtaBig`, `TabelaScroll`, `Tabela`...).
// Import explícito: contar com o CSS carregado por outra tela é acoplamento invisível
// (lição do BUG-V74/at-67).
import '../../recepcao/pages/Recepcao.css';
import { emDias, seloDoPrazo, tituloDoPrazo } from '../../../components/selo-prazo/SeloPrazoDaEtapa';
import './AndamentoVeiculosPage.css';

const headers = ['Placa', 'Esteira atual', 'Previsão da esteira', 'Dias na oficina', 'Na oficina desde'];
const defaultColumnWidths = [110, 190, 180, 150, 150];

const dataCurta = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};


const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta tela mostra',
    conteudo: (
      <>
        Os veículos que estão <strong>em atendimento na oficina agora</strong>, com a etapa em que
        cada um se encontra, o prazo previsto para essa etapa e há quantos dias o veículo está na
        oficina. Veículos já <strong>entregues não aparecem</strong> — nem na busca: sem etapa em
        andamento, não haveria prazo nem andamento a mostrar.
      </>
    ),
  },
  {
    titulo: 'Quando aparece "+ Mecânica" na etapa',
    conteudo: (
      <>
        <p>
          Um veículo pode estar sendo trabalhado em <strong>mais de uma esteira ao mesmo tempo</strong>:
          a etapa em que ele está e outra <strong>aberta em paralelo</strong> pelo Chefe de Oficina,
          para que outro setor trabalhe nele sem que o carro precise sair da etapa.
        </p>
        <p>
          Quando isso acontece, a coluna da etapa mostra o complemento — por exemplo,{' '}
          <strong>Desmontagem + Mecânica</strong>. Passe o mouse para ver a lista completa. O{' '}
          <strong>prazo exibido continua sendo o da etapa</strong>, e não a soma dos dois serviços.
        </p>
      </>
    ),
  },
  {
    titulo: 'Ver o que já foi feito no veículo',
    conteudo: (
      <>
        <p>
          Clique na <strong>placa</strong> para abrir o detalhe do veículo. Ali aparecem as{' '}
          <strong>sub-tarefas</strong> de cada etapa e quais já foram concluídas — é o nível de
          detalhe que a etapa sozinha não dá.
        </p>
        <p>
          A lista só existe quando a etapa tem sub-tarefas cadastradas. Se não houver nenhuma, é
          porque aquele serviço não foi detalhado — não porque nada foi feito.
        </p>
        <p>
          Esta tela continua sendo <strong>somente de consulta</strong>: quem registra o que foi
          feito é a equipe da oficina, e quem fala com o associado é a Recepção.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que é a "Previsão da esteira"',
    conteudo: (
      <>
        É o <strong>prazo total previsto para a etapa atual</strong>, definido pelo Chefe de
        Oficina quando o veículo entra nela — <strong>não</strong> é o número de dias que ainda
        faltam. Um veículo com previsão de 5 dias que entrou na etapa há 4 continua exibindo
        "5 dias".
        <br />
        <br />
        Para saber quanto já passou, use a coluna <strong>Dias na oficina</strong>: ela conta desde
        a entrada do veículo, somando todas as etapas pelas quais ele passou.
        <br />
        <br />
        Quando a etapa é <strong>Aguardando</strong>, a previsão já inclui o tempo de espera da
        peça pendente mais distante — é o mesmo número que o Chefe de Oficina vê.
      </>
    ),
  },
  {
    titulo: 'Atrasado, Em risco e Sem prazo definido',
    conteudo: (
      <>
        Ao lado da previsão aparece um selo quando o prazo da etapa merece atenção. A régua é a
        mesma do painel da direção: <strong>Em risco</strong> quando o veículo já consumiu 80% do
        prazo da etapa, e <strong>Atrasado</strong> quando passou dele (o selo diz por quantos
        dias). Sem selo, o veículo está dentro do prazo.
        <br />
        <br />
        <strong>Sem prazo definido</strong> é a etapa para a qual ninguém estimou um prazo. Não quer
        dizer que está em dia: quer dizer que não há com o que comparar. Passe o mouse sobre a
        previsão para ver há quantos dias o veículo está na etapa.
      </>
    ),
  },
  {
    titulo: 'O selo "Pós-evento"',
    conteudo: (
      <>
        Indica que o veículo <strong>voltou em garantia</strong> para avaliar um serviço já
        entregue. É um atendimento novo, com evento próprio. <strong>Pós-evento externo</strong>{' '}
        significa que o serviço original não está registrado neste sistema.
      </>
    ),
  },
  {
    titulo: 'Buscar por placa',
    conteudo: (
      <>
        Digite a placa no campo de busca para localizar um veículo específico. A busca aceita a
        placa <strong>parcial</strong> e não diferencia maiúsculas nem hífen — "abc-1d23", "ABC1D23"
        e "abc1" encontram o mesmo veículo.
      </>
    ),
  },
  {
    titulo: 'Quando a consulta falha',
    conteudo: (
      <>
        Se a consulta não puder ser feita, a tela avisa com uma faixa vermelha e{' '}
        <strong>não</strong> afirma que não há veículos — porque nesse caso o sistema não conseguiu
        verificar. Só trate a lista vazia como resposta quando <strong>não houver</strong> aviso de
        erro na tela.
      </>
    ),
  },
  {
    titulo: 'Esta tela é somente leitura',
    conteudo: (
      <>
        O setor de Eventos <strong>acompanha</strong> o andamento: não há nada a editar aqui, e
        nenhuma ação desta tela altera o veículo. Mudança de etapa, prazo e liberação são do Chefe
        de Oficina e da Administração — para pedir alteração, fale com eles.
      </>
    ),
  },
  {
    titulo: 'Você é avisado quando alguém comenta num veículo',
    conteudo: (
      <>
        <p>
          O <strong>sino</strong> no canto superior direito, presente em todas as telas, mostra
          seus avisos, e o número em cima dele é quantos você ainda não leu. Aviso novo também
          aparece num quadro no canto da tela, e o número vai para o título da aba do navegador.
          O mesmo número aparece no{' '}
          <strong>menu lateral</strong>, no item desta tela. Toda vez que alguém escreve um{' '}
          <strong>comentário</strong> num veículo, o aviso chega ali — é o retorno vivo da oficina
          sobre o carro, do tipo &quot;o associado veio hoje e informou que o desamassado está
          ruim&quot;.
        </p>
        <p>
          Clique no aviso para lê-lo por inteiro e marcá-lo como lido. O comentário completo, com
          quem escreveu e quando, fica no <strong>detalhe do veículo</strong>: clique na placa
          nesta tela. Se a aba <strong>Feedback</strong> da Auditoria estiver liberada para o seu
          perfil, você também pode responder por lá.
        </p>
        <p>
          <strong>Resposta</strong> a comentário é avisada apenas a quem escreveu o comentário
          original — então você recebe o aviso das respostas aos seus próprios comentários, e não
          das conversas dos outros.
        </p>
      </>
    ),
  },
];

/**
 * Andamento dos veículos — a única tela do setor de EVENTOS (at-85, ADR 0039).
 *
 * ⚠️ A regra da at-60 tem consequência prática aqui: lista vazia pode significar "nenhum veículo
 * em atendimento" (resposta legítima) **ou** "a consulta falhou" (não-resposta). Confundir as duas
 * faz o setor concluir que a oficina está livre, ou que o veículo que procura já saiu — e agir
 * sobre isso. Por isso `erro` e `respondeu` são estados distintos de "lista vazia".
 */
const AndamentoVeiculosPage: React.FC = () => {
  const [termo, setTermo] = useState('');
  /** Termo efetivamente consultado — é dele que a resposta da tela fala, não do que se digita. */
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [itens, setItens] = useState<VeiculoEmAndamento[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);

  /*
   * transversal/at-90 — o aviso aberto pelo sino do topo chega como `?placa=`, e aplica a busca
   * desta tela, a única coisa que ela sabe fazer com uma placa. O parâmetro sai da URL.
   */
  const [parametrosDaUrl, setParametrosDaUrl] = useSearchParams();
  useEffect(() => {
    const placa = parametrosDaUrl.get('placa');
    if (!placa) return;
    setParametrosDaUrl({}, { replace: true });
    setTermo(placa);
    setPagina(1);
    setBuscaAplicada(placa);
  }, [parametrosDaUrl, setParametrosDaUrl]);
  const [limite, setLimite] = useState(10);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /** `false` enquanto a consulta atual não tiver respondido com sucesso. */
  const [respondeu, setRespondeu] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  // at-89: detalhe do veículo (somente leitura) com o que já foi feito.
  const [veiculoAberto, setVeiculoAberto] = useState<VeiculoEmAndamento | null>(null);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const consultar = useCallback(async (busca: string, page: number, limit: number) => {
    setCarregando(true);
    try {
      const resposta = await buscarVeiculosEmAndamento({ busca, page, limit });
      setItens(resposta.itens ?? []);
      setTotal(resposta.total ?? 0);
      setErro(null);
      setRespondeu(true);
    } catch {
      // Estado seguro E sinal visível: a lista esvazia, mas `respondeu` fica false, então a tela
      // NÃO afirma que não há veículos em atendimento.
      setItens([]);
      setTotal(0);
      setRespondeu(false);
      setErro(
        'Não foi possível consultar o andamento dos veículos. Enquanto este aviso estiver na ' +
          'tela, a lista não serve como resposta — recarregue a página e, se persistir, acione a TI.',
      );
      // TEXTO FIXO, não `errorFrom` (regra da at-65): este aviso é sobre o ESTADO ENGANOSO DA
      // TELA, não sobre uma ação que falhou. `errorFrom` prioriza a mensagem do backend — num 403
      // ela vira "Você não tem permissão para executar esta ação", que substitui a única
      // informação que importa aqui: a lista na tela não é resposta.
      notify.error(
        'Não foi possível consultar o andamento dos veículos. A lista pode estar incompleta — ' +
          'não conclua que um veículo saiu da oficina enquanto este aviso aparecer.',
        { toastId: 'setor-eventos-consulta-falhou' },
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void consultar(buscaAplicada, pagina, limite);
  }, [consultar, buscaAplicada, pagina, limite]);

  const aplicarBusca = () => {
    setPagina(1);
    setBuscaAplicada(termo.trim());
  };

  const limparBusca = () => {
    setTermo('');
    setPagina(1);
    setBuscaAplicada('');
  };

  const linhasRenderizadas = useMemo(
    () =>
      itens.map((v) => [
        v.plate || '-',
        v.esteiraNome || '-',
        // A largura da coluna é medida pelo conteúdo — o selo entra na conta.
        `${emDias(v.previsaoDiasNaEsteira)} ${seloDoPrazo(v)?.texto ?? ''}`.trim(),
        emDias(v.diasNaOficina),
        dataCurta(v.entryDate),
      ]),
    [itens],
  );

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: linhasRenderizadas,
  });

  const totalPaginas = Math.max(1, Math.ceil(total / limite));
  const primeiroDaPagina = total === 0 ? 0 : (pagina - 1) * limite + 1;
  const ultimoDaPagina = Math.min(pagina * limite, total);

  const textoDoContador = (): string => {
    if (carregando) return 'Consultando...';
    if (!respondeu) return 'Não foi possível consultar';
    if (total === 0) return '0 veículos';
    return `${primeiroDaPagina}-${ultimoDaPagina} de ${total} veículos`;
  };

  return (
    <div className="ConteDo recepcao-conte-do">
      {/* at-65: a faixa fica DENTRO do conteúdo; substituir a tela apagaria busca e tabela. */}
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Andamento dos Veículos
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
        <div className="recepcao-search-bar">
          <div className="ItemSearch" onClick={aplicarBusca}>
            <img className="imgIcon" src={SearchIcon} alt="Ícone de busca" width="20" height="20" />
          </div>
          <input
            type="text"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && aplicarBusca()}
            placeholder="Busque pela placa do veículo"
            aria-label="Buscar placa"
            className="recepcao-search-input"
          />
        </div>
        {/* transversal/at-90: o sino desta tela saiu — o do topo traz a placa como `?placa=`. */}
      </div>

      {/* A explicação fica FIXA na tela, não só na hint: é a leitura errada mais provável
          desta tabela, e quem consulta de relance não abre o "Como Funciona". */}
      <p className="andamento-hint">
        A <strong>previsão da esteira</strong> é o prazo total previsto para a etapa atual, definido
        pelo Chefe de Oficina — não é o número de dias restantes. O selo ao lado diz se o veículo
        está <strong>em risco</strong> (80% do prazo) ou <strong>atrasado</strong>; sem selo, está no
        prazo. <strong>Sem prazo definido</strong> não é "em dia": ninguém estimou a etapa.
      </p>

      <div className="VisualizarOrdenar">
        <div
          className="Frame1000004037"
          style={{
            alignSelf: 'stretch',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 12,
            display: 'flex',
            flexWrap: 'wrap',
          }}
        >
          <div className="CtaBig">
            <div className="Frame1000003987">
              <div className="ComponentName">Itens por página:</div>
              <select
                id="limite"
                value={limite}
                onChange={(e) => {
                  setLimite(Number(e.target.value));
                  setPagina(1);
                }}
                className="ComponentNameImgIcon"
                style={{ cursor: 'pointer' }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {buscaAplicada && (
            <div className="CtaBig MouseHover" onClick={limparBusca}>
              <div className="Frame1000003987">
                <div className="ComponentName">Limpar busca</div>
              </div>
            </div>
          )}
        </div>

        <div className="QuantidadeDeCarros">
          <div
            className="Frame1000004037"
            style={{
              alignSelf: 'stretch',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 12,
              display: 'flex',
            }}
          >
            <div className="CtaQuantidadeDeCarros">
              <div className="Frame1000003987">
                <div className="ComponentName">{textoDoContador()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          width: '100%',
          paddingTop: 30,
          background: 'var(--tema-superficie, white)',
          borderRadius: 16,
          border: '1px var(--tema-linha-2, #CACACA) solid',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex',
          marginBottom: 20,
        }}
      >
        <div className="TabelaScroll" ref={tableScrollRef}>
          <div
            className="TabelaContent"
            style={{
              ['--table-columns' as any]: tableColumns,
              width: tableWidth,
              minWidth: tableWidth,
            }}
          >
            <div className="Tabela TabelaHeader">
              <div className="Placa">
                <div className="TTulo">
                  {headers.map((header, index) => (
                    <div
                      key={header}
                      className="Opcoes"
                      ref={(el) => {
                        headerRefs.current[index] = el;
                      }}
                    >
                      {header}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {carregando && (
              <div className="Tabela">
                <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                  <div className="Data">Consultando...</div>
                </div>
              </div>
            )}

            {/* "Nenhum veículo" é afirmação de negócio: só quando a consulta respondeu. */}
            {!carregando && itens.length === 0 && respondeu && (
              <div className="Tabela">
                <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                  <div className="Data">
                    {buscaAplicada
                      ? 'Nenhum veículo em atendimento encontrado para esta placa.'
                      : 'Nenhum veículo em atendimento na oficina no momento.'}
                  </div>
                </div>
              </div>
            )}

            {!carregando && itens.length === 0 && !respondeu && (
              <div className="Tabela">
                <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                  <div className="Data">A consulta não pôde ser feita — veja o aviso acima.</div>
                </div>
              </div>
            )}

            {!carregando &&
              itens.map((v) => (
                <div key={v.id} className="Tabela">
                  <div className="Item">
                    {/* at-89: a placa abre o detalhe com o que já foi feito. Continua sendo
                        leitura — o papel EVENTOS não ganhou nenhuma ação. */}
                    {/* ⚠️ A classe `Data` é obrigatória: é ela que carrega a tipografia da
                        célula (Roboto 12px, line-height 2). Um <button> não herda fonte do pai,
                        então sem ela a placa sai com a fonte padrão do navegador e destoa das
                        demais tabelas do sistema. */}
                    <button
                      type="button"
                      className="Data andamento-placa-btn"
                      title={`Ver o que já foi feito em ${v.plate}`}
                      onClick={() => setVeiculoAberto(v)}
                    >
                      {v.plate || '-'}
                    </button>
                    {/* at-134: ao lado, nunca empilhado (célula de tabela é UMA linha). */}
                    <SeloPosEventoPorOrigem origem={v.posEvento?.origem} />
                  </div>

                  <div className="Item">
                    {/*
                      at-99: o veículo pode estar sendo trabalhado em mais de uma esteira ao mesmo
                      tempo. Sem o "+ Mecânica" o setor lê a etapa e conclui que só ela está em
                      curso — que era a leitura errada quando a oficina resolvia isso movendo o
                      veículo de um lado para o outro.

                      ⚠️ Célula é UMA linha (regra do Victor, 12/08/2026): o complemento vai ao
                      lado, menor e em cinza, com o valor inteiro no `title`.
                    */}
                    <div
                      className="Data"
                      title={
                        v.esteirasParalelas?.length
                          ? `${v.esteiraNome ?? ''} — também em: ${v.esteirasParalelas.join(', ')}`
                          : v.esteiraNome ?? ''
                      }
                    >
                      {v.esteiraNome || '-'}
                      {v.esteirasParalelas && v.esteirasParalelas.length > 0 && (
                        <span className="andamento-paralelas">
                          {v.esteirasParalelas.length === 1
                            ? ` + ${v.esteirasParalelas[0]}`
                            : ` + ${v.esteirasParalelas.length} esteiras`}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="Item">
                    <div className="Data" title={tituloDoPrazo(v)}>
                      {emDias(v.previsaoDiasNaEsteira)}
                      {(() => {
                        const selo = seloDoPrazo(v);
                        return selo ? (
                          <span className={`cilia-badge ${selo.classe} andamento-selo-prazo`}>{selo.texto}</span>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  <div className="Item">
                    <div className="Data">{emDias(v.diasNaOficina)}</div>
                  </div>

                  <div className="Item">
                    <div className="Data">{dataCurta(v.entryDate)}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {total > limite && (
          <Paginacao paginaAtual={pagina} totalPaginas={totalPaginas} onMudarPagina={setPagina} />
        )}
      </div>

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Andamento dos Veículos"
        secoes={helpSections}
      />

      <AndamentoVeiculoModal
        isOpen={Boolean(veiculoAberto)}
        carId={veiculoAberto?.id ?? null}
        plate={veiculoAberto?.plate}
        esteiraNome={veiculoAberto?.esteiraNome}
        origemDoPosEvento={veiculoAberto?.posEvento?.origem ?? null}
        onClose={() => setVeiculoAberto(null)}
      />
    </div>
  );
};

export default AndamentoVeiculosPage;
